import { Box } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { usePerformGlobalRefresh } from "@/components/header/hooks";
import { PullToRefresh } from "@/ui/PullToRefresh/PullToRefresh";

type Props = {
	children: ReactNode;
	/**
	 * The stem of the scroller's and the list's `#id` and test id (`{idPrefix}-scroller`, `{idPrefix}-list`). The home
	 * and Open Shifts are different routes, so only one list is ever mounted, but each keeps its own name.
	 */
	idPrefix: string;
};

/**
 * The only thing on the gaps-first home that scrolls: the list under the pinned chrome.
 *
 * Pull-to-refresh lives HERE and not on the page, as it does on classic (its header sits outside the
 * scroller). A page-wide pull also fired from the week strip, the search row and the tabs — controls a
 * finger drags across on purpose — and it has caused trouble before. Scoped to the list, a pull can only
 * start where the list is and only once the list is at its top.
 *
 * Being its own scroller is also what makes the group headers' `sticky top: 0` correct: against a
 * page-wide scroller they stuck at the page's top, underneath the pinned strip.
 */
export const GapsListScroller: FC<Props> = ({ children, idPrefix }) => {
	// Same refresh as classic: the new home changes the layout, it must not take the gesture away.
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	// State, not refs: `PullToRefresh` attaches in an effect keyed on these elements, and a ref's
	// `.current` read during render is still null on the first pass. Classic only works because its header
	// happens to re-render after measuring itself; nothing here does, so the elements must cause the render.
	const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);
	const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

	return (
		// pulltorefreshjs is driven by `#id` CSS selectors, so `PullToRefresh` needs real ids on both
		// elements — without them it warns once and never attaches. Named by the page, not `useId()`, whose
		// output is not a valid unescaped CSS id; each page renders one list.
		<Box
			id={`${idPrefix}-scroller`}
			data-testid={`${idPrefix}-scroller`}
			ref={setScrollerEl}
			sx={{
				position: "relative",
				flex: 1,
				minHeight: 0,
				width: "100%",
				overflowY: "auto",
				overscrollBehaviorY: "contain",
				WebkitOverflowScrolling: "touch",
			}}
		>
			<PullToRefresh key="gaps-list-pull-to-refresh" childrenContainerEl={contentEl} scrollerEl={scrollerEl} onRefresh={performGlobalRefresh} />
			<Box id={`${idPrefix}-list`} data-testid={`${idPrefix}-list`} ref={setContentEl}>
				{children}
			</Box>
		</Box>
	);
};
